import * as _ from 'lodash';
import * as utils from '../../utils';
import { StepAction } from './stepAction';
import { getLogger } from '../../commons/logger';
import { featureFlagsService } from '../../commons/featureFlagsService';
import type { RunContext } from './stepAction';
import type { RunCodeRes, HangingPromiseRes } from './scripts/runCode';
import type { ConsoleItem } from '../../agent/routers/cliJsCode/service';
import type { UserCodeStep } from 'clickim/src/common/models/userCodeStep';
import type { PlaybackContext, IncomingParams } from 'clickim/src/background/playbackContext';
import type { ISerializedStep } from 'clickim/src/common/models/step';

const logger = getLogger('base-js-step-action');

interface EventMessage {
    transactionId: string;
    id: string;
    eventType: string;
    code: string;
    incomingParams: IncomingParams | undefined;
    exportsGlobal: {};
    exportsTest: {};
    context: {
        config: PlaybackContext['config'];
        data: PlaybackContext['data'];
    };
    testResultId: string;
    browser: string;
    browserMajor: number;
}

interface ExecutionResponse {
    success: boolean;
    nodeVersion?: string;
    shouldRetry?: boolean;
    type?: 'promise';
    reason?: 'stopped';
    status?: 'done' | 'failed';
    tstConsoleLogs?: ConsoleItem[];
    navigateToDifferentDomain?: boolean;
    result?: Partial<{ resultValue: any; exports: object; exportsTest: object; exportsGlobal: object }>;
}

export abstract class BaseJsStepAction extends StepAction<UserCodeStep, RunContext & { retryIndex: number }> {
    protected startTimestamp?: number;

    private isExceedingMaxResultSize(data: Pick<ExecutionResponse, 'result' | 'tstConsoleLogs'>, context: RunContext) {
        try {
            const shouldEnforceMaxSize = context.project.defaults?.enforceMaximumJsResultSize;
            const maximumJsResultSize = featureFlagsService.flags.maximumJsResultSize.getValue();
            const dataSizeExceeded = JSON.stringify(data).length > maximumJsResultSize;
            if (!shouldEnforceMaxSize) {
                if (dataSizeExceeded) {
                    logger.warn(`js result size exceeded ${maximumJsResultSize}, stepId: ${this.step.id}`);
                }
                return false;
            }
            return dataSizeExceeded;
        } catch (e) {
            return false;
        }
    }

    private executeGetStatus(transactionId: string) {
        return this.driver.executeJS((_transactionId: string) => {
            const sessionItem = `data-testim-${_transactionId}`;
            try {
                return window.sessionStorage.getItem(sessionItem);
            } catch (err) {
                if (
                    err.message.includes('sessionStorage') || // Chrome + Firefox
                    err.message.includes('The operation is insecure') // Safari
                ) {
                    const storage = document.head.querySelector('#testim-storage-backup');
                    if (!storage) {
                        return '{}';
                    }
                    return storage.getAttribute(sessionItem);
                }
                throw err;
            }
        }, transactionId);
    }

    private constructJSFunParams(eventData: EventMessage) {
        const incomingParams = eventData.incomingParams!;

        const params = [
            'context', ...incomingParams.as.functionParameters,
            'exports', 'exportsTest', 'exportsGlobal',
        ];

        const args = [eventData.context, ...incomingParams.as.functionArguments];

        params.push(eventData.code);
        args.forEach(arg => {
            if (_.isObject(arg)) {
                utils.removePropertyFromObject(arg, 'seleniumElement', _.isEqual);
            }
        });
        return {
            function: {
                params,
                args,
            },
            transactionId: eventData.transactionId,
            browser: eventData.browser,
            browserMajor: eventData.browserMajor,
        };
    }


    private checkStatus(transactionId: string) {
        const { context, stepActionUtils, step: { id: stepId = '' } } = this;
        const retryInterval = context.config.retryTimeout;
        let timeToPlayStep = context.data.timeToPlayStep! - retryInterval;
        const checkScriptStatus = async (): Promise<ExecutionResponse> => {
            let selRes;
            try {
                selRes = await this.executeGetStatus(transactionId);
            } catch (err) {
                logger.warn('failed to get js status', { err });
                selRes = { value: '{ "status": "exception" }' };
            }
            let res;
            try {
                res = JSON.parse(selRes?.value || '{}') as RunCodeRes | HangingPromiseRes;
            } catch (e) {
                logger.warn('non object value', { selRes });
                res = { status: 'exception' } as Omit<HangingPromiseRes, 'type' | 'status'> & { status: 'exception' };
            }
            const abortReason = stepActionUtils.abortedSteps.find(abortedStep => abortedStep.id === stepId);
            if (abortReason) {
                return abortReason;
            }
            if (!res) {
                return { success: true };
            }
            if (res.status === 'done') {
                return res;
            }
            if (res.status === 'failed') {
                return { success: false, shouldRetry: true, result: res.result };
            }
            if (timeToPlayStep - retryInterval <= 0) {
                return Object.assign(res, { success: false, shouldRetry: true });
            }
            timeToPlayStep -= retryInterval;
            await utils.delay(retryInterval);
            return checkScriptStatus();
        };

        return checkScriptStatus();
    }

    private async executeInAut(eventMessage: EventMessage) {
        const useExperimentalPreCompilation = featureFlagsService.flags.experimentalPreCodeCompilation.isEnabled();
        const experimentalAsyncCustomCode = featureFlagsService.flags.experimentalAsyncCustomCode.isEnabled();
        const rawParams = this.constructJSFunParams(eventMessage);
        const hasLocateParams = rawParams.function.args.some(x => Boolean(x?.locatedElement));
        let funcToRunString = 'undefined';
        if (useExperimentalPreCompilation) {
            const paramNames = rawParams.function.params.slice(0, -1);
            funcToRunString = experimentalAsyncCustomCode ? `async function(${paramNames.join(',')}) {
                ${eventMessage.code}
            };` : `function(${paramNames.join(',')}) {
                ${eventMessage.code}
            };`;
            // remove code from call.
            rawParams.function.params.pop();
        }

        const { codeSnippets } = this.sessionPlayerInit;
        const { runCode: runCodeScript } = await import('./scripts/runCode');

        const runCode = `
            ${hasLocateParams ? `var getLocatedElement = ${codeSnippets.getLocatedElementCode};` : ';'}
            var runCode = ${runCodeScript.toString()};
            var eventData = arguments[0];
            var funcToRun = ${funcToRunString};
            return runCode.call(null, eventData, funcToRun);
        `;
        if (!useExperimentalPreCompilation) {
            return this.driver.executeJS<(params: typeof rawParams) => RunCodeRes>(runCode, rawParams);
        }

        try {
            return await this.driver.executeJS<(params: typeof rawParams) => RunCodeRes>(runCode, rawParams);
        } catch (err) {
            return this.handleExecutionError(err);
        }
    }

    protected abstract isFailedResult(result: any): boolean;

    private codeExecDone(resp: ExecutionResponse) {
        const { stepResult: constants } = this.sessionPlayerInit.commonConstants;
        const { context } = this;
        const { result = {}, tstConsoleLogs, nodeVersion, navigateToDifferentDomain } = resp;

        if (result.exports) {
            context.data.exports = result.exports;
        }

        const resultObj = {
            nodeVersion,
            tstConsoleLogs,
            data: context.data,
        };

        if (this.isFailedResult(result.resultValue)) {
            return {
                ...resultObj,
                success: false,
                errorType: constants.JS_ASSERTION_FAILED,
            };
        }
        if (this.isExceedingMaxResultSize({ result, tstConsoleLogs }, context)) {
            return {
                ...resultObj,
                success: false,
                errorType: constants.JS_RESULT_MAX_SIZE_EXCEEDED,
            };
        }
        return {
            ...resultObj,
            success: true,
            exportsTest: result.exportsTest,
            exportsGlobal: result.exportsGlobal,
            ...(navigateToDifferentDomain && { navigateToDifferentDomain }),
        };
    }

    private codeExecFailed(resp: ExecutionResponse) {
        const { stepResult: constants } = this.sessionPlayerInit.commonConstants;
        const { context } = this;
        if (resp.type === 'promise') {
            return {
                data: context.data,
                success: false,
                shouldRetry: true,
                isPendingPromise: true,
                errorType: constants.JS_ASSERTION_FAILED,
            };
        }
        if (resp.reason === 'stopped') {
            return { ...resp, errorType: constants.STOPPED };
        }
        const { result = {}, tstConsoleLogs } = resp;
        const message = {
            tstConsoleLogs,
            data: context.data,
            exportsGlobal: result.exportsGlobal,
            exportsTest: result.exportsTest,
            success: false,
            errorType: constants.UNWRAPPED_AUT_REJECT,
            resultInfo: { error: result.resultValue },
        };
        return message;
    }

    protected checkCodeResponse(resp: ExecutionResponse) {
        return resp?.success ? this.codeExecDone(resp) : this.codeExecFailed(resp);
    }

    protected getTransactionId(step: ISerializedStep<UserCodeStep>, context: RunContext) {
        let transactionIdStepSegment = step.id;
        if (!step.id) {
            logger.warn('step.id doesn\'t exist on step', { step, context });
            transactionIdStepSegment = context.stepResultId;
        }
        if ('stepResultId' in context && context.stepResultId?.length) {
            transactionIdStepSegment = context.stepResultId;
        } else {
            logger.warn('stepResultId is not present in context', { step, context });
        }
        return `${context.testResultId}:${transactionIdStepSegment}`;
    }

    override async performAction(): Promise<any> {
        const { step, context } = this;

        this.startTimestamp = Date.now();

        const browserAndOS = await this.driver.getBrowserAndOS();
        const eventMessage: EventMessage = {
            transactionId: this.getTransactionId(step, context),
            id: step.id!,
            eventType: step.type,
            code: step.code!,
            incomingParams: context.incomingParams,
            exportsGlobal: this.exportsGlobal,
            exportsTest: this.exportsTest,
            context: {
                config: context.config,
                data: context.data,
            },
            testResultId: context.testResultId,
            browser: browserAndOS.browser,
            browserMajor: browserAndOS.browserMajor,
        };

        if (!context.isPendingPromise) {
            await this.executeInAut(eventMessage);
        }

        const resp = await this.checkStatus(eventMessage.transactionId);
        return this.checkCodeResponse(resp);
    }

    private handleExecutionError(err: any): never {
        const canExtractError = err?.name === 'javascript error';

        if (canExtractError) {
            throw new Error(err.message);
        }

        throw err;
    }
}
